import { ApiResponseError, apiUrl, fetchWithTimeout, readApiJson } from "./api";
import {
  packDeliveryPartial,
  packDeliverySucceeded,
  parseLeadDelivery,
  type LeadDelivery,
} from "./leadContract";
import type { LeadKind, LeadPayload } from "../types";

export type LeadSubmitStatus = "success" | "partial" | "failure";

export function shouldBlockLeadSubmit(
  sending: boolean,
  sent: LeadKind | null,
  kind: LeadKind,
): boolean {
  return sending || sent === kind;
}

export type LeadSubmitResult = {
  status: LeadSubmitStatus;
  delivery: LeadDelivery | null;
  reason: "ok" | "partial" | "rejected" | "html" | "malformed" | "not_json" | "http" | "network" | "timeout";
};

const FAIL: LeadSubmitResult = { status: "failure", delivery: null, reason: "network" };

export async function submitLead(
  payload: LeadPayload,
  deps: { fetch?: typeof fetch; url?: string; timeoutMs?: number } = {},
): Promise<LeadSubmitResult> {
  const fetchImpl = deps.fetch ?? fetch;
  const url = deps.url ?? apiUrl("/lead");
  try {
    const res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      },
      deps.timeoutMs ?? 12_000,
      fetchImpl,
    );
    return interpretLeadResponse(payload.kind, res);
  } catch (error) {
    if (error instanceof ApiResponseError) {
      return { status: "failure", delivery: null, reason: error.kind === "timeout" ? "timeout" : error.kind };
    }
    if (error instanceof DOMException && error.name === "AbortError") {
      return { status: "failure", delivery: null, reason: "timeout" };
    }
    return FAIL;
  }
}

export async function interpretLeadResponse(kind: LeadKind, res: Response): Promise<LeadSubmitResult> {
  let body: unknown;
  try {
    body = await readApiJson<unknown>(res);
  } catch (error) {
    if (error instanceof ApiResponseError) {
      return { status: "failure", delivery: null, reason: error.kind === "html" ? "html" : error.kind };
    }
    return { status: "failure", delivery: null, reason: "malformed" };
  }

  const delivery = parseLeadDelivery(body);
  if (!delivery) {
    return { status: "failure", delivery: null, reason: res.ok ? "malformed" : "http" };
  }
  if (res.ok && packDeliverySucceeded(kind, { ...delivery, ok: true })) {
    return { status: "success", delivery, reason: "ok" };
  }
  if (packDeliveryPartial(kind, delivery)) {
    return { status: "partial", delivery, reason: "partial" };
  }
  return { status: "failure", delivery, reason: res.ok ? "rejected" : "http" };
}
